/* ============================================================
   PRODUCT FORM - COMPLETE INTERACTIONS
   ============================================================ */

(() => {
  'use strict';

  const SELECTORS = {
    root: '.pf-root',

    // Main form
    form: 'form[action*="/cart/add"]',
    variantInput: 'input[name="id"]',

    // Desktop size
    sizeDropdown: '[data-size-dropdown]',
    sizeTrigger: '[data-size-trigger]',
    sizeMenu: '[data-size-menu]',
    sizeText: '[data-size-text]',
    sizeSelect: '[data-size-select]',
    sizeOption: '[data-size-value]',

    // Colors
    colorSwatch: '.pf-color-swatch[data-color]',

    // Quantity
    quantityInput: '[data-quantity-input]',
    quantityDisplay: '[data-quantity-display]',
    quantityDecrease: '[data-quantity-decrease]',
    quantityIncrease: '[data-quantity-increase]',

    // Main ATC
    productAtc: '[data-product-atc]',

    // Size chart
    sizeChartTrigger: '[data-drawer-trigger]',
    sizeChartDrawer: '[data-size-chart-drawer]',
    sizeChartOverlay: '[data-drawer-overlay]',
    sizeChartClose: '[data-drawer-close]',

    // Mobile size drawer
    sizeDrawer: '[data-size-drawer]',
    sizeDrawerTrigger: '[data-floating-size-trigger]',
    sizeDrawerClose: '[data-size-drawer-close]',
    sizeDrawerOption: '[data-size-drawer-value]',

    // Mobile color drawer
    colorDrawer: '[data-color-drawer]',
    colorDrawerTrigger: '[data-floating-color-trigger]',
    colorDrawerClose: '[data-color-drawer-close]',
    colorDrawerOption: '[data-color-drawer-value]',

    // Floating ATC
    floatingAtc: '[data-floating-atc-btn]',
    floatingAtcText: '[data-floating-atc-text]',
    floatingSizeText: '[data-floating-size-text]',
    floatingColorText: '[data-floating-color-text]',
    floatingBuyNow: '[data-floating-buy-now]',

    // Mobile ATC
    mobileAtc: '[data-mobile-atc-button]',
    mobileAtcText: '[data-mobile-atc-text]',

    // Product details drawer
    detailsTrigger: '[data-mobile-details-trigger]',
    detailsDrawer: '[data-mobile-details-drawer]',
    detailsClose: '[data-mobile-details-close]',

    // Custom mobile tabs
    customTabTrigger: '[data-mobile-custom-tab-trigger]',

    // Shop the look
    shopLookTrigger: '[data-shop-look-trigger]'
  };


  /* ============================================================
     HELPERS
     ============================================================ */

  function q(selector, context = document) {
    return context.querySelector(selector);
  }

  function qa(selector, context = document) {
    return Array.from(context.querySelectorAll(selector));
  }

  function normalize(value) {
    return String(value || '').trim().toLowerCase();
  }

  function setText(elements, value) {
    elements.forEach(el => {
      if (el) {
        el.textContent = value;
      }
    });
  }

  function lockBody() {
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
  }

  function unlockBody() {
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
  }

  function escapeSelector(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(value);
    }

    return String(value).replace(/["\\]/g, '\\$&');
  }


  /* ============================================================
     GENERIC DRAWER HELPERS
     ============================================================ */

  function openDrawer(drawer) {
    if (!drawer) return;

    drawer.classList.add('active', 'open', 'is-open');

    drawer.style.visibility = 'visible';
    drawer.style.opacity = '1';
    drawer.style.pointerEvents = 'auto';

    const content = drawer.querySelector(
      '.pf-size-drawer-content, ' +
      '.pf-color-drawer-content, ' +
      '.pf-mobile-details-content'
    );

    if (content) {
      content.style.transform = 'translateX(0)';
    }

    drawer.setAttribute('aria-hidden', 'false');

    lockBody();
  }

  function closeDrawer(drawer) {
    if (!drawer) return;

    drawer.classList.remove('active', 'open', 'is-open');

    drawer.style.removeProperty('visibility');
    drawer.style.removeProperty('opacity');
    drawer.style.removeProperty('pointer-events');

    const content = drawer.querySelector(
      '.pf-size-drawer-content, ' +
      '.pf-color-drawer-content, ' +
      '.pf-mobile-details-content'
    );

    if (content) {
      content.style.removeProperty('transform');
    }

    drawer.setAttribute('aria-hidden', 'true');

    unlockBody();
  }


  /* ============================================================
     PRODUCT CONTROLLER
     ============================================================ */

  class ProductFormController {

    constructor(root) {
      this.root = root;

      this.form =
        q('#ProductForm', root) ||
        q(SELECTORS.form, root);

      if (!this.form) {
        console.warn('[Product Form] Product form not found.');
        return;
      }

      this.variantInput =
        q(SELECTORS.variantInput, this.form);

      this.product = null;
      this.variants = [];

      this.colorOptionIndex = null;
      this.sizeOptionIndex = null;

      this.selectedColor = null;
      this.selectedSize = null;

      this.isAddingToCart = false;

      this.init();
    }


    /* ==========================================================
       INITIALIZATION
       ========================================================== */

    async init() {
      this.detectCurrentSelections();

      await this.loadProduct();

      this.detectOptionIndexes();

      this.bindSizeDropdown();
      this.bindColors();
      this.bindQuantity();

      this.bindSizeChart();

      this.bindMobileSizeDrawer();
      this.bindMobileColorDrawer();

      this.bindMainATC();
      this.bindMobileATC();
      this.bindFloatingATC();

      this.bindBuyNow();

      this.bindDetailsDrawer();
      this.bindCustomTabDrawers();

      this.bindShopLook();

      this.bindKeyboard();

      this.syncInitialState();

      console.log(
        '[Product Form] Initialized successfully.'
      );
    }


    /* ==========================================================
       PRODUCT DATA
       ========================================================== */

    async loadProduct() {
      try {
        let url = window.location.pathname;

        url = url.replace(/\/$/, '');

        if (!url.endsWith('.js')) {
          url += '.js';
        }

        const response = await fetch(url, {
          headers: {
            Accept: 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(
            `Product request failed: ${response.status}`
          );
        }

        this.product = await response.json();
        this.variants = this.product.variants || [];

      } catch (error) {
        console.error(
          '[Product Form] Could not load product JSON:',
          error
        );
      }
    }


    detectOptionIndexes() {
      if (!this.product || !this.product.options) {
        return;
      }

      this.product.options.forEach((option, index) => {
        const optionName =
          typeof option === 'string'
            ? option
            : option.name;

        const name = normalize(optionName);

        if (
          name === 'color' ||
          name === 'colour'
        ) {
          this.colorOptionIndex = index;
        }

        if (name === 'size') {
          this.sizeOptionIndex = index;
        }
      });
    }


    detectCurrentSelections() {
      const selectedColor =
        q(
          '.pf-color-swatch.selected[data-color]',
          this.root
        ) ||
        q(
          '.pf-color-swatch[aria-checked="true"][data-color]',
          this.root
        );

      if (selectedColor) {
        this.selectedColor =
          selectedColor.dataset.color;
      }

      const sizeSelect =
        q(SELECTORS.sizeSelect, this.root);

      if (sizeSelect && sizeSelect.value) {
        this.selectedSize = sizeSelect.value;
      }
    }


    /* ==========================================================
       VARIANT MATCHING
       ========================================================== */

    getMatchingVariant() {
      if (!this.variants.length) {
        return null;
      }

      return this.variants.find(variant => {

        if (
          this.colorOptionIndex !== null &&
          this.selectedColor &&
          normalize(
            variant.options[this.colorOptionIndex]
          ) !== normalize(this.selectedColor)
        ) {
          return false;
        }

        if (
          this.sizeOptionIndex !== null &&
          this.selectedSize &&
          normalize(
            variant.options[this.sizeOptionIndex]
          ) !== normalize(this.selectedSize)
        ) {
          return false;
        }

        return true;
      }) || null;
    }


    sizeAvailable(size) {
      if (!this.variants.length) {
        return true;
      }

      return this.variants.some(variant => {

        if (!variant.available) {
          return false;
        }

        if (
          this.sizeOptionIndex !== null &&
          normalize(
            variant.options[this.sizeOptionIndex]
          ) !== normalize(size)
        ) {
          return false;
        }

        if (
          this.colorOptionIndex !== null &&
          this.selectedColor &&
          normalize(
            variant.options[this.colorOptionIndex]
          ) !== normalize(this.selectedColor)
        ) {
          return false;
        }

        return true;
      });
    }


    /* ==========================================================
       DESKTOP SIZE DROPDOWN
       ========================================================== */

    bindSizeDropdown() {
      qa(
        SELECTORS.sizeDropdown,
        this.root
      ).forEach(dropdown => {

        const trigger =
          q(SELECTORS.sizeTrigger, dropdown);

        const menu =
          q(SELECTORS.sizeMenu, dropdown);

        if (!trigger || !menu) {
          return;
        }

        trigger.addEventListener(
          'click',
          event => {
            event.preventDefault();
            event.stopPropagation();

            const isOpen =
              dropdown.classList.contains('is-open');

            this.closeAllSizeDropdowns();

            if (!isOpen) {
              dropdown.classList.add('is-open');

              trigger.setAttribute(
                'aria-expanded',
                'true'
              );

              menu.style.display = 'block';
              menu.style.visibility = 'visible';
              menu.style.opacity = '1';
              menu.style.pointerEvents = 'auto';
              menu.style.maxHeight = 'unset';
            }
          }
        );

        qa(
          SELECTORS.sizeOption,
          dropdown
        ).forEach(option => {

          option.addEventListener(
            'click',
            event => {
              event.preventDefault();
              event.stopPropagation();

              if (
                option.disabled ||
                option.classList.contains(
                  'sold-out'
                ) ||
                option.dataset.sizeAvailable ===
                  'false'
              ) {
                return;
              }

              this.selectSize(
                option.dataset.sizeValue
              );

              this.closeAllSizeDropdowns();
            }
          );

        });
      });

      document.addEventListener(
        'click',
        event => {
          if (
            !event.target.closest(
              SELECTORS.sizeDropdown
            )
          ) {
            this.closeAllSizeDropdowns();
          }
        }
      );
    }


    closeAllSizeDropdowns() {
      qa(
        SELECTORS.sizeDropdown,
        this.root
      ).forEach(dropdown => {

        dropdown.classList.remove('is-open');

        const trigger =
          q(SELECTORS.sizeTrigger, dropdown);

        const menu =
          q(SELECTORS.sizeMenu, dropdown);

        if (trigger) {
          trigger.setAttribute(
            'aria-expanded',
            'false'
          );
        }

        if (menu) {
          menu.style.removeProperty('display');
          menu.style.removeProperty('visibility');
          menu.style.removeProperty('opacity');
          menu.style.removeProperty(
            'pointer-events'
          );
          menu.style.removeProperty('max-height');
        }
      });
    }


    /* ==========================================================
       SELECT SIZE
       ========================================================== */

    selectSize(size) {
      if (!size) return;

      if (!this.sizeAvailable(size)) {
        return;
      }

      this.selectedSize = size;

      qa(
        SELECTORS.sizeSelect,
        this.root
      ).forEach(select => {

        select.value = size;

        select.dispatchEvent(
          new Event('change', {
            bubbles: true
          })
        );
      });

      setText(
        qa(SELECTORS.sizeText, this.root),
        size
      );

      setText(
        qa(
          SELECTORS.floatingSizeText,
          this.root
        ),
        size
      );

      qa(
        SELECTORS.sizeOption,
        this.root
      ).forEach(option => {

        const selected =
          normalize(option.dataset.sizeValue) ===
          normalize(size);

        option.classList.toggle(
          'selected',
          selected
        );

        option.classList.toggle(
          'active',
          selected
        );

        option.setAttribute(
          'aria-selected',
          selected ? 'true' : 'false'
        );
      });

      qa(
        SELECTORS.sizeDrawerOption,
        this.root
      ).forEach(option => {

        const selected =
          normalize(
            option.dataset.sizeDrawerValue
          ) === normalize(size);

        option.classList.toggle(
          'selected',
          selected
        );

        option.classList.toggle(
          'active',
          selected
        );
      });

      this.updateVariant();

      this.closeSizeDrawer();
      this.closeSizeChart();
    }


    /* ==========================================================
       COLOR
       ========================================================== */

    bindColors() {
      qa(
        SELECTORS.colorSwatch,
        this.root
      ).forEach(swatch => {

        swatch.addEventListener(
          'click',
          event => {
            event.preventDefault();

            this.selectColor(
              swatch.dataset.color
            );
          }
        );
      });
    }


    selectColor(color) {
      if (!color) return;

      this.selectedColor = color;

      qa(
        SELECTORS.colorSwatch,
        this.root
      ).forEach(swatch => {

        const selected =
          normalize(swatch.dataset.color) ===
          normalize(color);

        swatch.classList.toggle(
          'selected',
          selected
        );

        swatch.setAttribute(
          'aria-checked',
          selected ? 'true' : 'false'
        );

        swatch.tabIndex =
          selected ? 0 : -1;
      });

      setText(
        qa('[data-color-name]', this.root),
        color
      );

      setText(
        qa(
          SELECTORS.floatingColorText,
          this.root
        ),
        color
      );

      qa(
        SELECTORS.colorDrawerOption,
        this.root
      ).forEach(option => {

        const selected =
          normalize(
            option.dataset.colorDrawerValue
          ) === normalize(color);

        option.classList.toggle(
          'active',
          selected
        );

        option.classList.toggle(
          'selected',
          selected
        );
      });

      /*
       * Clear selected size if unavailable
       * for newly selected color.
       */
      if (
        this.selectedSize &&
        !this.sizeAvailable(
          this.selectedSize
        )
      ) {
        this.clearSize();
      }

      this.updateSizeAvailability();
      this.updateVariant();

      this.closeColorDrawer();

      this.filterImagesByColor(color);
    }


    clearSize() {
      this.selectedSize = null;

      qa(
        SELECTORS.sizeSelect,
        this.root
      ).forEach(select => {
        select.value = '';
      });

      setText(
        qa(SELECTORS.sizeText, this.root),
        'Select Size'
      );

      setText(
        qa(
          SELECTORS.floatingSizeText,
          this.root
        ),
        'Select Size'
      );

      qa(
        `${SELECTORS.sizeOption}, ${SELECTORS.sizeDrawerOption}`,
        this.root
      ).forEach(option => {

        option.classList.remove(
          'selected',
          'active'
        );

        option.setAttribute(
          'aria-selected',
          'false'
        );
      });
    }


    /* ==========================================================
       SIZE AVAILABILITY
       ========================================================== */

    updateSizeAvailability() {
      qa(
        SELECTORS.sizeOption,
        this.root
      ).forEach(option => {

        const size =
          option.dataset.sizeValue;

        if (!size) return;

        const available =
          this.sizeAvailable(size);

        option.disabled = !available;

        option.dataset.sizeAvailable =
          available ? 'true' : 'false';

        option.classList.toggle(
          'sold-out',
          !available
        );

        option.setAttribute(
          'aria-disabled',
          available ? 'false' : 'true'
        );
      });

      qa(
        SELECTORS.sizeDrawerOption,
        this.root
      ).forEach(option => {

        const size =
          option.dataset.sizeDrawerValue;

        if (!size) return;

        const available =
          this.sizeAvailable(size);

        option.disabled = !available;

        option.dataset.sizeAvailable =
          available ? 'true' : 'false';

        option.classList.toggle(
          'sold-out',
          !available
        );

        option.setAttribute(
          'aria-disabled',
          available ? 'false' : 'true'
        );
      });
    }


    /* ==========================================================
       UPDATE VARIANT
       ========================================================== */

    updateVariant() {
      if (
        this.sizeOptionIndex !== null &&
        !this.selectedSize
      ) {
        this.updateButtons(null);
        return;
      }

      const variant =
        this.getMatchingVariant();

      if (!variant) {
        this.updateButtons(null);
        return;
      }

      if (this.variantInput) {
        this.variantInput.value =
          variant.id;

        this.variantInput.dispatchEvent(
          new Event('change', {
            bubbles: true
          })
        );
      }

      try {
        const url =
          new URL(window.location.href);

        url.searchParams.set(
          'variant',
          variant.id
        );

        history.replaceState(
          {},
          '',
          url.toString()
        );

      } catch (error) {
        // Ignore URL errors.
      }

      this.updateButtons(variant);

      this.root.dispatchEvent(
        new CustomEvent(
          'pf:variant-change',
          {
            bubbles: true,

            detail: {
              variant,
              color: this.selectedColor,
              size: this.selectedSize
            }
          }
        )
      );
    }


    /* ==========================================================
       BUTTON STATES
       ========================================================== */

    updateButtons(variant) {
      const hasSize =
        this.sizeOptionIndex !== null;

      const needsSize =
        hasSize && !this.selectedSize;

      const available =
        !!variant && variant.available;


      // Main ATC
      qa(
        SELECTORS.productAtc,
        this.root
      ).forEach(button => {

        if (
          button.classList.contains(
            'is-loading'
          )
        ) {
          return;
        }

        const text =
          q('.pf-atc-text', button);

        if (needsSize) {
          button.disabled = true;

          if (text) {
            text.textContent =
              'SELECT SIZE';
          }

        } else if (!available) {
          button.disabled = true;

          if (text) {
            text.textContent =
              'SOLD OUT';
          }

        } else {
          button.disabled = false;

          if (text) {
            text.textContent =
              'ADD TO BAG';
          }
        }
      });


      // Mobile ATC
      qa(
        SELECTORS.mobileAtc,
        this.root
      ).forEach(button => {

        if (
          button.classList.contains(
            'is-loading'
          )
        ) {
          return;
        }

        const text =
          q(
            SELECTORS.mobileAtcText,
            button
          ) ||
          q(
            SELECTORS.mobileAtcText,
            this.root
          );

        if (needsSize) {
          button.disabled = false;

          if (text) {
            text.textContent =
              'Select Size';
          }

        } else if (!available) {
          button.disabled = true;

          if (text) {
            text.textContent =
              'Sold Out';
          }

        } else {
          button.disabled = false;

          if (text) {
            text.textContent =
              'ADD TO BAG';
          }
        }
      });


      // Floating ATC
      qa(
        SELECTORS.floatingAtc,
        this.root
      ).forEach(button => {

        if (
          button.classList.contains(
            'is-loading'
          )
        ) {
          return;
        }

        const text =
          q(
            SELECTORS.floatingAtcText,
            button
          ) ||
          q(
            SELECTORS.floatingAtcText,
            this.root
          );

        if (needsSize) {
          button.disabled = false;

          if (text) {
            text.textContent =
              'Select Size';
          }

        } else if (!available) {
          button.disabled = true;

          if (text) {
            text.textContent =
              'Sold Out';
          }

        } else {
          button.disabled = false;

          if (text) {
            text.textContent =
              'Add to Bag';
          }
        }
      });
    }


    /* ==========================================================
       QUANTITY
       ========================================================== */

    bindQuantity() {
      qa(
        SELECTORS.quantityDecrease,
        this.root
      ).forEach(button => {

        button.addEventListener(
          'click',
          event => {
            event.preventDefault();

            const container =
              button.closest(
                '.pf-quantity-section'
              );

            const input =
              q(
                SELECTORS.quantityInput,
                container || this.root
              );

            if (!input) return;

            const min =
              parseInt(
                input.min || '1',
                10
              );

            const current =
              parseInt(
                input.value || '1',
                10
              );

            input.value =
              Math.max(
                min,
                current - 1
              );

            this.updateQuantityDisplay(
              input
            );
          }
        );
      });


      qa(
        SELECTORS.quantityIncrease,
        this.root
      ).forEach(button => {

        button.addEventListener(
          'click',
          event => {
            event.preventDefault();

            const container =
              button.closest(
                '.pf-quantity-section'
              );

            const input =
              q(
                SELECTORS.quantityInput,
                container || this.root
              );

            if (!input) return;

            const max =
              parseInt(
                input.max || '99',
                10
              );

            const current =
              parseInt(
                input.value || '1',
                10
              );

            input.value =
              Math.min(
                max,
                current + 1
              );

            this.updateQuantityDisplay(
              input
            );
          }
        );
      });
    }


    updateQuantityDisplay(input) {
      qa(
        SELECTORS.quantityDisplay,
        input.closest(
          '.pf-quantity-section'
        ) || this.root
      ).forEach(display => {

        display.textContent =
          input.value;
      });

      input.dispatchEvent(
        new Event('change', {
          bubbles: true
        })
      );
    }


    getQuantity() {
      const input =
        q(
          SELECTORS.quantityInput,
          this.root
        );

      let quantity =
        parseInt(
          input?.value || '1',
          10
        );

      if (
        Number.isNaN(quantity) ||
        quantity < 1
      ) {
        quantity = 1;
      }

      return quantity;
    }


    /* ==========================================================
       SIZE CHART
       ========================================================== */

    bindSizeChart() {
      const drawer =
        q(SELECTORS.sizeChartDrawer);

      const overlay =
        q(SELECTORS.sizeChartOverlay);

      if (!drawer) return;

      qa(
        SELECTORS.sizeChartTrigger,
        this.root
      ).forEach(trigger => {

        trigger.addEventListener(
          'click',
          event => {
            event.preventDefault();

            drawer.classList.add(
              'active',
              'open',
              'is-open'
            );

            drawer.style.transform =
              'translateX(0)';

            drawer.style.visibility =
              'visible';

            drawer.style.opacity = '1';

            drawer.style.pointerEvents =
              'auto';

            if (overlay) {
              overlay.classList.add(
                'active',
                'open',
                'is-open'
              );

              overlay.style.visibility =
                'visible';

              overlay.style.opacity =
                '1';

              overlay.style.pointerEvents =
                'auto';
            }

            lockBody();
          }
        );
      });

      qa(
        SELECTORS.sizeChartClose
      ).forEach(button => {

        button.addEventListener(
          'click',
          () => {
            this.closeSizeChart();
          }
        );
      });

      if (overlay) {
        overlay.addEventListener(
          'click',
          () => {
            this.closeSizeChart();
          }
        );
      }
    }


    closeSizeChart() {
      const drawer =
        q(SELECTORS.sizeChartDrawer);

      const overlay =
        q(SELECTORS.sizeChartOverlay);

      if (drawer) {
        drawer.classList.remove(
          'active',
          'open',
          'is-open'
        );

        drawer.style.removeProperty(
          'transform'
        );

        drawer.style.removeProperty(
          'visibility'
        );

        drawer.style.removeProperty(
          'opacity'
        );

        drawer.style.removeProperty(
          'pointer-events'
        );
      }

      if (overlay) {
        overlay.classList.remove(
          'active',
          'open',
          'is-open'
        );

        overlay.style.removeProperty(
          'visibility'
        );

        overlay.style.removeProperty(
          'opacity'
        );

        overlay.style.removeProperty(
          'pointer-events'
        );
      }

      unlockBody();
    }


    /* ==========================================================
       MOBILE SIZE DRAWER
       ========================================================== */

    bindMobileSizeDrawer() {
      qa(
        SELECTORS.sizeDrawerTrigger,
        this.root
      ).forEach(trigger => {

        trigger.addEventListener(
          'click',
          event => {
            event.preventDefault();

            openDrawer(
              q(
                SELECTORS.sizeDrawer,
                this.root
              )
            );
          }
        );
      });

      qa(
        SELECTORS.sizeDrawerClose,
        this.root
      ).forEach(close => {

        close.addEventListener(
          'click',
          () => {
            this.closeSizeDrawer();
          }
        );
      });

      qa(
        SELECTORS.sizeDrawerOption,
        this.root
      ).forEach(option => {

        option.addEventListener(
          'click',
          event => {
            event.preventDefault();

            if (
              option.disabled ||
              option.classList.contains(
                'sold-out'
              )
            ) {
              return;
            }

            this.selectSize(
              option.dataset.sizeDrawerValue
            );
          }
        );
      });
    }


    closeSizeDrawer() {
      closeDrawer(
        q(
          SELECTORS.sizeDrawer,
          this.root
        )
      );
    }


    /* ==========================================================
       MOBILE COLOR DRAWER
       ========================================================== */

    bindMobileColorDrawer() {
      qa(
        SELECTORS.colorDrawerTrigger,
        this.root
      ).forEach(trigger => {

        trigger.addEventListener(
          'click',
          event => {
            event.preventDefault();

            openDrawer(
              q(
                SELECTORS.colorDrawer,
                this.root
              )
            );
          }
        );
      });

      qa(
        SELECTORS.colorDrawerClose,
        this.root
      ).forEach(close => {

        close.addEventListener(
          'click',
          () => {
            this.closeColorDrawer();
          }
        );
      });

      qa(
        SELECTORS.colorDrawerOption,
        this.root
      ).forEach(option => {

        option.addEventListener(
          'click',
          event => {
            event.preventDefault();

            this.selectColor(
              option.dataset.colorDrawerValue
            );
          }
        );
      });
    }


    closeColorDrawer() {
      closeDrawer(
        q(
          SELECTORS.colorDrawer,
          this.root
        )
      );
    }


    /* ==========================================================
       LOADING STATE
       ========================================================== */

    setATCLoading(button, loading = true) {
      if (!button) return;

      const textEl =
        button.querySelector(
          '.pf-atc-text'
        ) ||
        button.querySelector(
          '[data-floating-atc-text]'
        ) ||
        button.querySelector(
          '[data-mobile-atc-text]'
        );

      if (loading) {
        if (
          textEl &&
          !button.dataset.originalText
        ) {
          button.dataset.originalText =
            textEl.textContent.trim();
        }

        button.classList.add(
          'is-loading'
        );

        button.disabled = true;

        button.setAttribute(
          'aria-busy',
          'true'
        );

        if (textEl) {
          textEl.innerHTML =
            '<span class="pf-atc-spinner" aria-hidden="true"></span>';
        }

      } else {
        button.classList.remove(
          'is-loading'
        );

        button.removeAttribute(
          'aria-busy'
        );

        if (
          textEl &&
          button.dataset.originalText
        ) {
          textEl.textContent =
            button.dataset.originalText;
        }

        button.disabled = false;

        delete button.dataset.originalText;
      }
    }


    /* ==========================================================
       MAIN ADD TO CART
       ========================================================== */

    bindMainATC() {
      /*
       * Prevent native submission.
       * All ATC is handled through cart/add.js.
       */
      this.form.addEventListener(
        'submit',
        event => {
          event.preventDefault();

          const button =
            q(
              SELECTORS.productAtc,
              this.form
            ) ||
            q(
              SELECTORS.productAtc,
              this.root
            );

          if (
            button &&
            !button.disabled
          ) {
            this.addToCart(button);
          }
        }
      );

      qa(
        SELECTORS.productAtc,
        this.root
      ).forEach(button => {

        /*
         * If button isn't actually inside
         * the form, handle its click directly.
         */
        if (!this.form.contains(button)) {
          button.addEventListener(
            'click',
            event => {
              event.preventDefault();

              if (!button.disabled) {
                this.addToCart(button);
              }
            }
          );
        }
      });
    }


    /* ==========================================================
       MOBILE ADD TO CART
       ========================================================== */

    bindMobileATC() {
      qa(
        SELECTORS.mobileAtc,
        this.root
      ).forEach(button => {

        button.addEventListener(
          'click',
          event => {
            event.preventDefault();

            if (
              this.sizeOptionIndex !== null &&
              !this.selectedSize
            ) {
              openDrawer(
                q(
                  SELECTORS.sizeDrawer,
                  this.root
                )
              );

              return;
            }

            if (!button.disabled) {
              this.addToCart(button);
            }
          }
        );
      });
    }


    /* ==========================================================
       FLOATING ADD TO CART
       ========================================================== */

    bindFloatingATC() {
      qa(
        SELECTORS.floatingAtc,
        this.root
      ).forEach(button => {

        button.addEventListener(
          'click',
          event => {
            event.preventDefault();

            if (
              this.sizeOptionIndex !== null &&
              !this.selectedSize
            ) {
              openDrawer(
                q(
                  SELECTORS.sizeDrawer,
                  this.root
                )
              );

              return;
            }

            if (!button.disabled) {
              this.addToCart(button);
            }
          }
        );
      });
    }


    /* ==========================================================
       ADD TO CART
       Shopify AJAX -> redirect /cart
       ========================================================== */

    async addToCart(clickedButton) {
      if (this.isAddingToCart) {
        return;
      }

      /*
       * Require size selection.
       */
      if (
        this.sizeOptionIndex !== null &&
        !this.selectedSize
      ) {
        const sizeDrawer =
          q(
            SELECTORS.sizeDrawer,
            this.root
          );

        if (
          sizeDrawer &&
          window.matchMedia(
            '(max-width: 749px)'
          ).matches
        ) {
          openDrawer(sizeDrawer);

        } else {
          const trigger =
            q(
              SELECTORS.sizeTrigger,
              this.root
            );

          if (trigger) {
            trigger.click();
          }
        }

        return;
      }

      /*
       * Find exact variant.
       */
      const variant =
        this.getMatchingVariant();

      if (!variant) {
        console.error(
          '[Product Form] Matching variant not found.',
          {
            color: this.selectedColor,
            size: this.selectedSize
          }
        );

        alert(
          'Please select an available product option.'
        );

        return;
      }

      /*
       * Check availability.
       */
      if (!variant.available) {
        this.updateButtons(variant);

        return;
      }

      const quantity =
        this.getQuantity();

      /*
       * Keep form variant input synchronized.
       */
      if (this.variantInput) {
        this.variantInput.value =
          variant.id;
      }

      this.isAddingToCart = true;

      this.setATCLoading(
        clickedButton,
        true
      );

      try {
        const root =
          window.Shopify &&
          window.Shopify.routes &&
          window.Shopify.routes.root
            ? window.Shopify.routes.root
            : '/';

        const cartAddUrl =
          `${root}cart/add.js`;

        console.log(
          '[Product Form] Adding variant:',
          variant.id,
          'Quantity:',
          quantity
        );

        const response =
          await fetch(
            cartAddUrl,
            {
              method: 'POST',

              headers: {
                'Content-Type':
                  'application/json',

                Accept:
                  'application/json',

                'X-Requested-With':
                  'XMLHttpRequest'
              },

              body: JSON.stringify({
                items: [
                  {
                    id: Number(
                      variant.id
                    ),
                    quantity:
                      Number(quantity)
                  }
                ]
              })
            }
          );

        let result = null;

        try {
          result =
            await response.json();

        } catch (jsonError) {
          result = null;
        }

        if (!response.ok) {
          let message =
            'Unable to add this product to the cart.';

          if (result) {
            message =
              result.description ||
              result.message ||
              message;
          }

          throw new Error(message);
        }

        console.log(
          '[Product Form] Product added successfully:',
          result
        );

        /*
         * IMPORTANT:
         * Only redirect AFTER Shopify confirms
         * successful cart addition.
         */
        window.location.assign(
          `${root}cart`
        );

      } catch (error) {
        console.error(
          '[Product Form] Add to cart failed:',
          error
        );

        this.isAddingToCart = false;

        this.setATCLoading(
          clickedButton,
          false
        );

        this.updateVariant();

        alert(
          error.message ||
          'Unable to add this product to the cart. Please try again.'
        );
      }
    }


    /* ==========================================================
       BUY NOW
       ========================================================== */

    bindBuyNow() {
      qa(
        `${SELECTORS.floatingBuyNow}, [data-buy-now-btn]`,
        this.root
      ).forEach(button => {

        button.addEventListener(
          'click',
          async event => {
            event.preventDefault();

            if (
              this.sizeOptionIndex !== null &&
              !this.selectedSize
            ) {
              openDrawer(
                q(
                  SELECTORS.sizeDrawer,
                  this.root
                )
              );

              return;
            }

            const variant =
              this.getMatchingVariant();

            if (
              !variant ||
              !variant.available
            ) {
              return;
            }

            const quantity =
              this.getQuantity();

            try {
              button.disabled = true;

              const root =
                window.Shopify &&
                window.Shopify.routes &&
                window.Shopify.routes.root
                  ? window.Shopify.routes.root
                  : '/';

              const response =
                await fetch(
                  `${root}cart/add.js`,
                  {
                    method: 'POST',

                    headers: {
                      'Content-Type':
                        'application/json',

                      Accept:
                        'application/json',

                      'X-Requested-With':
                        'XMLHttpRequest'
                    },

                    body: JSON.stringify({
                      items: [
                        {
                          id: Number(
                            variant.id
                          ),
                          quantity:
                            Number(quantity)
                        }
                      ]
                    })
                  }
                );

              if (!response.ok) {
                let message =
                  'Unable to add product.';

                try {
                  const data =
                    await response.json();

                  message =
                    data.description ||
                    data.message ||
                    message;

                } catch (error) {
                  // Ignore JSON error.
                }

                throw new Error(message);
              }

              window.location.assign(
                `${root}checkout`
              );

            } catch (error) {
              console.error(
                '[Product Form] Buy now failed:',
                error
              );

              button.disabled = false;

              alert(
                error.message ||
                'Unable to continue to checkout.'
              );
            }
          }
        );
      });
    }


    /* ==========================================================
       PRODUCT DETAILS DRAWER
       ========================================================== */

    bindDetailsDrawer() {
      const drawer =
        q(
          SELECTORS.detailsDrawer,
          this.root
        );

      if (!drawer) return;

      qa(
        SELECTORS.detailsTrigger,
        this.root
      ).forEach(trigger => {

        trigger.addEventListener(
          'click',
          event => {
            event.preventDefault();

            openDrawer(drawer);
          }
        );
      });

      qa(
        SELECTORS.detailsClose,
        drawer
      ).forEach(close => {

        close.addEventListener(
          'click',
          () => {
            closeDrawer(drawer);
          }
        );
      });
    }


    /* ==========================================================
       CUSTOM MOBILE TAB DRAWERS
       ========================================================== */

    bindCustomTabDrawers() {
      qa(
        SELECTORS.customTabTrigger,
        this.root
      ).forEach(trigger => {

        trigger.addEventListener(
          'click',
          event => {
            event.preventDefault();

            const id =
              trigger.dataset
                .mobileCustomTabTrigger;

            if (!id) return;

            const safeId =
              escapeSelector(id);

            const drawer =
              q(
                `[data-mobile-custom-tab-drawer="${safeId}"]`,
                this.root
              );

            if (!drawer) return;

            openDrawer(drawer);

            qa(
              `[data-mobile-custom-tab-close="${safeId}"]`,
              drawer
            ).forEach(close => {

              if (
                close.dataset.bound ===
                'true'
              ) {
                return;
              }

              close.dataset.bound =
                'true';

              close.addEventListener(
                'click',
                () => {
                  closeDrawer(drawer);
                }
              );
            });
          }
        );
      });
    }


    /* ==========================================================
       SHOP THE LOOK
       ========================================================== */

    bindShopLook() {
      qa(
        SELECTORS.shopLookTrigger,
        this.root
      ).forEach(trigger => {

        trigger.addEventListener(
          'click',
          event => {
            event.preventDefault();

            const accordion =
              trigger.closest(
                '.pf-shop-look-accordion'
              );

            if (!accordion) return;

            accordion.classList.toggle(
              'is-open'
            );

            const content =
              q(
                '.pf-shop-look-content',
                accordion
              );

            if (content) {
              const open =
                accordion.classList.contains(
                  'is-open'
                );

              content.style.display =
                open ? '' : 'none';
            }
          }
        );
      });
    }


    /* ==========================================================
       COLOR IMAGE FILTERING
       ========================================================== */

    filterImagesByColor(color) {
      if (!color) return;

      const target =
        normalize(color);

      // Desktop images
      qa(
        '[data-image-color]',
        this.root
      ).forEach(item => {

        const imageColor =
          normalize(
            item.dataset.imageColor
          );

        const show =
          !imageColor ||
          imageColor === target;

        item.style.display =
          show ? '' : 'none';
      });


      // Mobile slides
      qa(
        '[data-slide-color]',
        this.root
      ).forEach(slide => {

        const imageColor =
          normalize(
            slide.dataset.slideColor
          );

        const show =
          !imageColor ||
          imageColor === target;

        slide.style.display =
          show ? '' : 'none';

        slide.classList.toggle(
          'color-filtered',
          !show
        );
      });


      // Mobile pagination
      qa(
        '[data-mobile-pagination-dot]',
        this.root
      ).forEach(dot => {

        const index =
          dot.dataset.mobilePaginationDot;

        if (
          index === undefined ||
          index === null
        ) {
          return;
        }

        const safeIndex =
          escapeSelector(index);

        const slide =
          q(
            `[data-slide-index="${safeIndex}"]`,
            this.root
          );

        const hidden =
          slide &&
          slide.style.display ===
            'none';

        dot.classList.toggle(
          'hidden',
          !!hidden
        );
      });


      const allDots =
        qa(
          '[data-mobile-pagination-dot]',
          this.root
        );

      allDots.forEach(dot => {
        dot.classList.remove(
          'active'
        );
      });

      const visibleDot =
        allDots.find(
          dot =>
            !dot.classList.contains(
              'hidden'
            )
        );

      if (visibleDot) {
        visibleDot.classList.add(
          'active'
        );
      }
    }


    /* ==========================================================
       INITIAL STATE
       ========================================================== */

    syncInitialState() {
      this.updateSizeAvailability();

      /*
       * Require customer to explicitly
       * choose a size.
       */
      if (
        this.sizeOptionIndex !== null
      ) {
        this.selectedSize = null;

        qa(
          SELECTORS.sizeSelect,
          this.root
        ).forEach(select => {
          select.value = '';
        });

        setText(
          qa(
            SELECTORS.sizeText,
            this.root
          ),
          'Select Size'
        );

        setText(
          qa(
            SELECTORS.floatingSizeText,
            this.root
          ),
          'Select Size'
        );

        this.updateButtons(null);

      } else {
        this.updateVariant();
      }

      if (this.selectedColor) {
        this.filterImagesByColor(
          this.selectedColor
        );
      }
    }


    /* ==========================================================
       KEYBOARD
       ========================================================== */

    bindKeyboard() {
      document.addEventListener(
        'keydown',
        event => {

          if (
            event.key !== 'Escape'
          ) {
            return;
          }

          this.closeAllSizeDropdowns();
          this.closeSizeChart();
          this.closeSizeDrawer();
          this.closeColorDrawer();

          qa(
            '.pf-mobile-details-drawer',
            this.root
          ).forEach(drawer => {
            closeDrawer(drawer);
          });
        }
      );
    }

  }


  /* ============================================================
     INITIALIZER
     ============================================================ */

  function initializeProductForms(
    context = document
  ) {
    qa(
      SELECTORS.root,
      context
    ).forEach(root => {

      if (
        root.dataset
          .productJsInitialized ===
        'true'
      ) {
        return;
      }

      root.dataset
        .productJsInitialized =
        'true';

      new ProductFormController(
        root
      );
    });
  }


  /* Normal storefront */

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initializeProductForms();
      }
    );

  } else {
    initializeProductForms();
  }


  /* Shopify Theme Editor */

  document.addEventListener(
    'shopify:section:load',
    event => {
      initializeProductForms(
        event.target
      );
    }
  );

})();